import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamRefreshStatus,
} from 'app/frontend/base/config/queries'
import { TeamRefreshType } from 'app/frontend/types/custom/TeamRefreshStatus'
import { CircleQuestionMark } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import Timestamp from 'app/frontend/base/components/Timestamp'
import { Button } from '@/components/ui/button'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Card from 'app/frontend/base/components/Card'
import { refreshTeamData } from 'app/frontend/base/api/team'
import { displayErrorToast } from 'app/frontend/base/helpers'

type Props = {
  type: TeamRefreshType
  horizontal?: boolean
}

const RefreshStatusBlock = ({ type, horizontal }: Props) => {
  const { team, guild } = useTeam()
  const [refreshingManually, setRefreshingManually] = useState<boolean | null>(
    false,
  )
  const {
    data: { user },
  } = usePageInfo()
  const { t } = useTranslation()
  const { data } = useTeamRefreshStatus(team.id)
  const queryClient = useQueryClient()
  const invalidateTeamData = () => {
    for (const key of [
      'teamAuditData',
      'teamKnownRecipes',
      'teamCharacterDetails',
    ]) {
      queryClient.invalidateQueries({ queryKey: [key, { id: team.id }] })
    }
  }
  const { mutate, isPending } = useMutation({
    mutationFn: () => refreshTeamData(team.id, type),
    onSuccess: (result) => {
      queryClient.setQueryData(['teamRefreshStatus', { id: team.id }], result)
      invalidateTeamData()
    },
    onError: (error) => {
      displayErrorToast(error, t)
      setRefreshingManually(false)
    },
  })

  const status = data?.status[type]

  const handleRefreshNow = () => {
    setRefreshingManually(true)
    mutate()
  }

  useEffect(() => {
    if (status) {
      if (
        type === 'collections' &&
        refreshingManually &&
        status.last_refreshed > 0
      ) {
        invalidateTeamData()
        setRefreshingManually(null)
      }
    }
  }, [status?.last_refreshed])

  const progressBar = (
    <CircularProgressbarWithChildren
      strokeWidth={10}
      className={cn('p-4 sm:p-8', horizontal ? 'w-56 shrink-0' : '-mt-4')}
      value={(status?.progress ?? 0) * 100}
      styles={{
        text: {
          fill: 'var(--color-muted-foreground)',
        },
        path: {
          stroke: 'var(--color-primary)',
          transition: 'stroke-dashoffset 2s ease 0s',
          strokeLinecap: 'butt',
        },
        trail: {
          stroke: 'var(--color-muted)',
        },
      }}
    >
      <div className='flex flex-col items-center justify-center'>
        <div className='text-muted-foreground'>Progress</div>
        <div className='text-2xl font-semibold'>
          {status?.progress ? `${Math.round(status.progress * 100)}%` : '0%'}
        </div>
      </div>
    </CircularProgressbarWithChildren>
  )

  const infoSection = (
    <div
      className={cn('flex flex-col gap-4 items-center', horizontal && 'flex-1')}
    >
      <div className='flex flex-col gap-0.5 items-center'>
        <Label className='text-muted-foreground'>Last refreshed</Label>
        <div
          className={cn(
            'font-semibold transition-opacity',
            status ? 'opacity-100' : 'opacity-0',
          )}
        >
          {status?.last_refresh ? (
            <Timestamp time={status.last_refresh} markOutdatedDays={null} />
          ) : (
            '-'
          )}
        </div>
      </div>

      <div className='flex flex-col gap-0.5 items-center'>
        <Label className='text-muted-foreground'>Position in queue</Label>
        <div
          className={cn(
            'font-semibold transition-opacity',
            status ? 'opacity-100' : 'opacity-0',
          )}
        >
          {status?.position ?? 'N/A'}
        </div>
      </div>

      <div className='flex flex-col gap-0.5 items-center justify-center'>
        <Label className='text-muted-foreground text-center'>
          Estimated next refresh
        </Label>
        <div
          className={cn(
            'font-semibold transition-opacity',
            status ? 'opacity-100' : 'opacity-0',
          )}
        >
          {status?.estimate ? (
            <Timestamp time={status.estimate} markOutdatedDays={null} />
          ) : status?.position === 0 ? (
            'in progress'
          ) : (
            'calculating...'
          )}
        </div>
      </div>

      {user?.canRefreshManually || guild?.canRefreshManually ? (
        <Button
          size='sm'
          onClick={handleRefreshNow}
          loading={
            isPending ||
            (status && (status.position === 0 || status.last_refreshed === 0))
          }
          disabled={refreshingManually === null}
          spinner='blizzard'
          variant='outline'
        >
          {refreshingManually === null ? 'Refreshed!' : 'Refresh now'}
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger className='cursor-not-allowed'>
            <Button size='sm' variant='outline' disabled>
              Refresh now
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Refreshing on demand is only available for users with a subscription
            tier of at least{' '}
            <span className='font-semibold text-quality-epic'>Epic</span>.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )

  if (horizontal) {
    return (
      <Card className='flex flex-row items-center gap-4'>
        {progressBar}
        <div className='flex flex-col items-center gap-1'>
          <h4 className='flex items-center gap-2'>
            {t(`dashboard.team.spreadsheet.status.${type}.title`)}
            <Tooltip>
              <TooltipTrigger>
                <CircleQuestionMark size={14} />
              </TooltipTrigger>
              <TooltipContent>
                {t(`dashboard.team.spreadsheet.status.${type}.description`)}
              </TooltipContent>
            </Tooltip>
          </h4>
          {infoSection}
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <h4 className='flex items-center justify-center gap-2'>
        {t(`dashboard.team.spreadsheet.status.${type}.title`)}
        <Tooltip>
          <TooltipTrigger>
            <CircleQuestionMark size={14} />
          </TooltipTrigger>
          <TooltipContent>
            {t(`dashboard.team.spreadsheet.status.${type}.description`)}
          </TooltipContent>
        </Tooltip>
      </h4>
      {progressBar}
      {infoSection}
    </Card>
  )
}

export default RefreshStatusBlock
